import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { Calendar, Download, Filter, Loader2, MoreHorizontal, Plus, RefreshCw, Search, Trash2, Upload } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { Q_PURCHASES, M_CREATE_PURCHASE, M_DELETE_PURCHASE } from '@/graphql/purchases.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const PAGE_SIZE = 8;
const tipoOptions = ['Factura A', 'Factura B', 'Factura C', 'Factura M', 'Nota de Crédito', 'Nota de Débito'];

export default function ListadoCompras() {
  const { can } = useAuth();
  const canCreate = can('crear_compra');
  const canEdit = can('editar_compra');
  const canViewPayables = can('CUENTAS_PAGAR_VER');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tipo, setTipo] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [page, setPage] = useState(1);
  const [estadoPago, setEstadoPago] = useState('');

  const [result, reexecuteQuery] = useQuery({
    query: Q_PURCHASES,
    variables: {
      filter: {
        q: debouncedSearch || undefined,
        tipo: tipo || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        estadoPago: estadoPago || undefined,
      },
      pagination: { page, perPage: PAGE_SIZE },
    },
  });

  const [, duplicatePurchase] = useMutation(M_CREATE_PURCHASE);
  const [, deletePurchase] = useMutation(M_DELETE_PURCHASE);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const purchases = result.data?.purchases?.items ?? [];
  const pageInfo = result.data?.purchases?.pageInfo;

  const handleDuplicate = async purchase => {
    const payload = {
      supplierId: purchase.supplierId,
      tipo: purchase.tipo,
      puntoVenta: purchase.puntoVenta,
      numero: purchase.numero,
      fecha: purchase.fecha,
      cae: purchase.cae,
      caeVto: purchase.caeVto,
      moneda: purchase.moneda,
      condicionCompra: purchase.condicionCompra,
      importes: purchase.importes,
      items: purchase.items || [],
      adjuntos: purchase.adjuntos || [],
      observaciones: `${purchase.observaciones || ''} (Duplicado)`,
    };
    await duplicatePurchase({ input: payload });
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const handleDelete = async id => {
    const confirmed = window.confirm('¿Seguro que querés eliminar esta compra?');
    if (!confirmed) return;
    await deletePurchase({ id });
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const handleExport = () => {
    if (!purchases.length) return;
    const headers = ['Fecha', 'Proveedor', 'Tipo', 'PV', 'Número', 'Neto', 'IVA', 'Total'];
    const rows = purchases.map(item => [
      item.fecha,
      item.supplier?.razon_social || '',
      item.tipo,
      item.puntoVenta,
      item.numero,
      formatCurrency(item.importes?.neto),
      formatCurrency((item.importes?.iva21 || 0) + (item.importes?.iva105 || 0) + (item.importes?.iva27 || 0)),
      formatCurrency(item.importes?.total),
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compras-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const paginationSummary = useMemo(() => {
    if (!pageInfo) return '';
    const start = (pageInfo.page - 1) * pageInfo.perPage + 1;
    const end = Math.min(pageInfo.totalItems, start + purchases.length - 1);
    return `Mostrando ${start}-${end} de ${pageInfo.totalItems}`;
  }, [pageInfo, purchases.length]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registro de compras"
        description="Consultá el historial de compras y descargá la información para conciliaciones."
        breadcrumbs={[
          { label: 'Tesorería', href: '/tesoreria' },
          { label: 'Registro de compras' },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Exportar CSV
            </button>
            {canCreate ? <Link
              to="/tesoreria/compras/nueva"
              className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nueva compra
            </Link> : null}
            {canCreate ? <Link
              to="/tesoreria/compras/carga-masiva"
              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              Carga masiva
            </Link> : null}
          </div>
        }
      >
        <span className="rounded-full bg-[rgba(var(--panel),0.55)] px-3 py-1 text-xs font-semibold text-[rgb(var(--brand))]">
          Reportes instantáneos
        </span>
      </PageHeader>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <label className="sr-only" htmlFor="purchase-search">
              Buscar
            </label>
            <div className="flex items-center gap-3 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-2 focus-within:border-[rgb(var(--brand))] focus-within:ring-2 focus-within:ring-[rgb(var(--ring))]">
              <Search className="h-4 w-4 text-[rgba(var(--muted),0.85)]" aria-hidden="true" />
              <input
                id="purchase-search"
                type="search"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Buscar por proveedor, número o CUIT"
                className="w-full bg-transparent text-sm text-[rgb(var(--fg))] placeholder:text-[rgba(var(--muted),0.75)] focus:outline-none"
              />
            </div>
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="sr-only" htmlFor="purchase-type">
              Tipo
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-3 py-2">
              <Filter className="h-4 w-4 text-[rgba(var(--muted),0.85)]" aria-hidden="true" />
              <select
                id="purchase-type"
                value={tipo}
                onChange={event => {
                  setTipo(event.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm text-[rgb(var(--fg))] focus:outline-none"
              >
                <option value="" className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                  Todos los tipos
                </option>
                {tipoOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="sr-only" htmlFor="fecha-desde">
              Fecha desde
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-3 py-2">
              <Calendar className="h-4 w-4 text-[rgba(var(--muted),0.85)]" aria-hidden="true" />
              <input
                id="fecha-desde"
                type="date"
                value={fechaDesde}
                onChange={event => {
                  setFechaDesde(event.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm text-[rgb(var(--fg))] focus:outline-none"
              />
            </div>
          </div>
          <div className="col-span-6 md:col-span-2">
            <select value={estadoPago} onChange={event => { setEstadoPago(event.target.value); setPage(1); }} className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none">
              <option value="">Todos los pagos</option>
              <option value="PENDIENTE">Pendientes</option>
              <option value="PARCIAL">Parciales</option>
              <option value="PAGADA">Pagadas</option>
            </select>
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="sr-only" htmlFor="fecha-hasta">
              Fecha hasta
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-3 py-2">
              <Calendar className="h-4 w-4 text-[rgba(var(--muted),0.85)]" aria-hidden="true" />
              <input
                id="fecha-hasta"
                type="date"
                value={fechaHasta}
                onChange={event => {
                  setFechaHasta(event.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm text-[rgb(var(--fg))] focus:outline-none"
              />
            </div>
          </div>
          <div className="col-span-12 md:col-span-1 flex items-center justify-end text-xs text-[rgba(var(--muted),0.85)]">
            {paginationSummary}
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] shadow-[0_18px_38px_rgba(var(--shadow),0.12)]">
          <table className="min-w-full divide-y divide-[rgba(var(--border),0.45)] text-sm text-[rgb(var(--fg))]">
            <thead className="bg-[rgba(var(--panel),0.9)] text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),0.75)]">
              <tr>
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-left">Proveedor</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">PV-Número</th>
                <th className="px-4 py-3 text-left">Neto</th>
                <th className="px-4 py-3 text-left">IVA</th>
                <th className="px-4 py-3 text-left">Total</th>
                <th className="px-4 py-3 text-left">Pago / saldo</th>
                <th className="px-4 py-3 text-left">Adjuntos</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(var(--border),0.35)]">
              {result.fetching ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-[rgba(var(--muted),0.85)]">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Cargando compras...
                    </div>
                  </td>
                </tr>
              ) : purchases.length ? (
                purchases.map(purchase => {
                  const ivaTotal = (purchase.importes?.iva21 || 0) + (purchase.importes?.iva105 || 0) + (purchase.importes?.iva27 || 0);
                  return (
                    <tr key={purchase.id} className="transition hover:bg-[rgba(var(--panel),0.75)]">
                      <td className="px-4 py-3">{purchase.fecha}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{purchase.supplier?.razon_social || '—'}</div>
                        <div className="text-xs text-[rgba(var(--muted),0.8)]">{purchase.supplier?.cuit || ''}</div>
                      </td>
                      <td className="px-4 py-3">{purchase.tipo}</td>
                      <td className="px-4 py-3">{purchase.puntoVenta}-{purchase.numero}</td>
                      <td className="px-4 py-3">{formatCurrency(purchase.importes?.neto)}</td>
                      <td className="px-4 py-3">{formatCurrency(ivaTotal)}</td>
                      <td className="px-4 py-3 font-semibold">{formatCurrency(purchase.importes?.total)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{purchase.estadoPago}</div>
                        <div className="text-xs text-[rgba(var(--muted),0.8)]">Saldo: {formatCurrency(purchase.saldoPendiente)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {purchase.adjuntos?.length ? `${purchase.adjuntos.length} archivo${purchase.adjuntos.length > 1 ? 's' : ''}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/tesoreria/compras/${purchase.id}`}
                            className="inline-flex h-9 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.7)] px-3 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" /> {canEdit ? 'Ver/Editar' : 'Ver'}
                          </Link>
                          {canViewPayables && Number(purchase.saldoPendiente || 0) > 0.004 ? <Link to={`/tesoreria/cuentas-pagar?purchaseId=${encodeURIComponent(purchase.id)}`} className="inline-flex h-9 items-center rounded-xl bg-[rgb(var(--brand))] px-3 text-xs font-semibold text-white">Ir a cuentas por pagar</Link> : null}
                          {canCreate ? <button
                            type="button"
                            onClick={() => handleDuplicate(purchase)}
                            className="inline-flex h-9 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.7)] px-3 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Duplicar
                          </button> : null}
                          {canEdit ? <button
                            type="button"
                            onClick={() => handleDelete(purchase.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.7)] text-[rgba(var(--muted),0.9)] transition hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Eliminar compra</span>
                          </button> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-12">
                    <EmptyState
                      title="No hay compras registradas"
                      description="Cargá tu primera compra para ver el resumen y poder exportar los datos."
                      action={
                        <Link
                          to="/tesoreria/compras/nueva"
                          className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          Registrar compra
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pageInfo && pageInfo.totalPages > 1 ? (
          <div className="mt-4 flex flex-col gap-3 rounded-2xl bg-[rgba(var(--panel),0.55)] p-4 text-sm text-[rgba(var(--muted),0.85)] sm:flex-row sm:items-center sm:justify-between">
            <div>
              Página {pageInfo.page} de {pageInfo.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(current => Math.max(1, current - 1))}
                disabled={pageInfo.page <= 1}
                className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.75)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage(current => Math.min(pageInfo.totalPages, current + 1))}
                disabled={pageInfo.page >= pageInfo.totalPages}
                className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.75)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                Siguiente
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}
